import type { QuoteFile } from 'paintscout'

/**
 * The one place the photo-content taxonomy is defined (ENG-1857 / #23702) — the vision classifier
 * (`classifyPhotoCategory`), the presentation-v2 authoring schema, and the image-matching pass all
 * import this, so they can never drift apart.
 *
 * A single axis (what surface/element the photo shows), not two (surface + room) — mixing "siding
 * vs. cabinets" with "kitchen vs. bathroom" doubles the ambiguity a vision model has to resolve for
 * a distinction nobody asked for. This list is a starting proposal: cheap to revise, since every
 * consumer reads from here rather than hardcoding values.
 */
export const PHOTO_CATEGORIES = [
  'siding',
  'soffit',
  'fascia',
  'roofing',
  'exterior-windows-doors',
  'deck-fence',
  'driveway-walkway',
  'exterior-other',
  'walls',
  'ceilings',
  'cabinets',
  'interior-trim',
  'interior-windows-doors',
  'floors',
  'interior-other',
  'people-process'
] as const

export type PhotoCategory = (typeof PHOTO_CATEGORIES)[number]

const EXTERIOR_CATEGORIES = new Set<PhotoCategory>([
  'siding',
  'soffit',
  'fascia',
  'roofing',
  'exterior-windows-doors',
  'deck-fence',
  'driveway-walkway',
  'exterior-other'
])

const INTERIOR_CATEGORIES = new Set<PhotoCategory>([
  'walls',
  'ceilings',
  'cabinets',
  'interior-trim',
  'interior-windows-doors',
  'floors',
  'interior-other'
])

/**
 * Coarse interior/exterior theme, derived from the fine-grained category rather than classified
 * independently — one judgment call per photo, so the two fields can never disagree.
 */
export function themeForCategory(category?: PhotoCategory): 'interior' | 'exterior' | undefined {
  if (!category) return undefined
  if (EXTERIOR_CATEGORIES.has(category)) return 'exterior'
  if (INTERIOR_CATEGORIES.has(category)) return 'interior'
  return undefined
}

/**
 * `QuoteFile.interiorExterior`'s value for a classified category: the derived theme, or `'other'`
 * when classification succeeded but the category has no theme (`people-process`). `undefined`
 * only when there's no category at all (never classified, or classification failed) — kept
 * distinct from `'other'` so callers can tell "classified as neither" from "never classified".
 */
export function interiorExteriorForCategory(category?: PhotoCategory): 'interior' | 'exterior' | 'other' | undefined {
  return themeForCategory(category) ?? (category ? 'other' : undefined)
}

export type CategoryPools = Map<PhotoCategory, QuoteFile[]>

/**
 * Groups classified real photos by category for `replaceSectionImages` to draw from, alongside
 * (not instead of) the existing flat, looped `images` sequence. A photo with no `photoCategory`
 * (unclassified, or classification failed) is only ever placed via the general sequence.
 */
export function buildCategoryPools(images: QuoteFile[]): CategoryPools {
  const pools: CategoryPools = new Map()
  for (const image of images) {
    if (!image.photoCategory) continue
    const bucket = pools.get(image.photoCategory)
    if (bucket) bucket.push(image)
    else pools.set(image.photoCategory, [image])
  }
  return pools
}

/**
 * Pops one photo of the given category, or `undefined` when there is no pool (a non-AI caller, or
 * one with no classified photos), no `category` (the authored slot isn't tagged), or the pool for
 * that category is already exhausted.
 *
 * Mutates `pools` in place (`Array.shift()`) — each pool is meant to be consumed exactly once, by
 * a single top-to-bottom walk of the presentation (see `replacePresentationImages`, which builds
 * the pools and threads them through one pass). A caller that walks the same pools a second time
 * (a retry, a dry-run preview) would see them already empty — build fresh pools per walk.
 *
 * With `placed`, a photo the general sequence already used is skipped, and the returned photo is
 * recorded — so a tagged slot and an untagged one never get the same photo (#24057).
 */
export function takeFromCategoryPool(
  pools: CategoryPools | undefined,
  category?: PhotoCategory,
  placed?: PlacedImages
): QuoteFile | undefined {
  if (!pools || !category) return undefined
  const pool = pools.get(category)
  let image = pool?.shift()
  while (image && placed?.has(imageKey(image))) image = pool?.shift()
  if (image) placed?.add(imageKey(image))
  return image
}

/** Keys of the photos already placed in one presentation walk. See `imageKey`. */
export type PlacedImages = Set<string>

/** Identifies one photo: its S3 key when it has one, else its URL. */
export function imageKey(image: QuoteFile): string {
  return image.s3PublicKey || image.src || image.key || ''
}
